import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import HouseOfIntelligence from "./pages/HouseOfIntelligence";
import IntegratedCRM from "./pages/IntegratedCRM";
import AccountCheck from "./pages/kyc/AccountCheck";
import ProfileIDCard from "./pages/sales/ProfileIDCard";
import BusinessIDCard from "./pages/sales/BusinessIDCard";
import PeopleList from "./pages/sales/PeopleList";
import BusinessList from "./pages/sales/BusinessList";
import Reportistica from "./pages/Reportistica";
import IntelligencePersonas from "./pages/intelligence/IntelligencePersonas";
import IntelligenceCompany from "./pages/intelligence/IntelligenceCompany";
import Tradeshow from "./pages/network/Tradeshow";
import NetworkEvents from "./pages/network/NetworkEvents";
import WarRoom from "./pages/WarRoom";
import PatentsPage from "./pages/tech/PatentsPage";
import TechnologiesPage from "./pages/tech/TechnologiesPage";
import RDTechnologiesPage from "./pages/tech/RDTechnologiesPage";
import CustomerSupportPage from "./pages/CustomerSupportPage";
import ContractTwin from "./pages/ContractTwin";
import DueDiligence from "./pages/DueDiligence";
import EntityLossSimulation from "./pages/EntityLossSimulation";
import CyberExposure from "./pages/CyberExposure";
import LiveWorld from "./pages/LiveWorld";
import NormativaPage from "./pages/normativa/NormativaPage";
import CompliancePage from "./pages/normativa/CompliancePage";
import TrainingCourses from "./pages/academy/TrainingCourses";
import DocumentListPage from "./pages/academy/DocumentListPage";
import { BookOpen, FileText, Archive, Library } from "lucide-react";
import HomePage from "./pages/HomePage";
import AdminGuard from "./components/AdminGuard";
import AdminLayout from "./pages/admin/AdminLayout";
import ContentsIndex from "./pages/admin/ContentsIndex";
import CollectionEditor from "./pages/admin/CollectionEditor";
import LimitsSettings from "./pages/admin/LimitsSettings";
import ServicesSettings from "./pages/admin/ServicesSettings";
import ServiceGuard from "./components/ServiceGuard";
import { ServicesProvider } from "./context/ServicesContext";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent animate-spin mx-auto mb-3" />
        <p className="text-[#A0AEC0] text-xs tracking-widest uppercase">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  // Single login entry — route by role.
  const homeForUser = user
    ? (user.role === "admin" ? "/admin" : "/dashboard")
    : "/login";
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={user ? <Navigate to={homeForUser} replace /> : <LoginPage />} />
      <Route path="/app" element={<Navigate to={homeForUser} replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/crm" element={<ProtectedRoute><Layout><IntegratedCRM /></Layout></ProtectedRoute>} />
      <Route path="/house-of-intelligence" element={<ProtectedRoute><Layout><HouseOfIntelligence /></Layout></ProtectedRoute>} />
      <Route path="/hoi" element={<Navigate to="/house-of-intelligence" replace />} />
      <Route path="/sales/profiles" element={<ProtectedRoute><Layout><ServiceGuard serviceId="sales"><ProfileIDCard /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/sales/businesses" element={<ProtectedRoute><Layout><ServiceGuard serviceId="sales"><BusinessIDCard /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/sales/list" element={<ProtectedRoute><Layout><ServiceGuard serviceId="sales"><BusinessList /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/reportistica" element={<ProtectedRoute><Layout><Reportistica /></Layout></ProtectedRoute>} />
      <Route path="/sales/people-list" element={<ProtectedRoute><Layout><ServiceGuard serviceId="sales"><PeopleList /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/kyc/account-check" element={<ProtectedRoute><Layout><ServiceGuard serviceId="sales"><AccountCheck /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/intelligence/personas" element={<ProtectedRoute><Layout><ServiceGuard serviceId="intelligence"><IntelligencePersonas key="sales-personas" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/intelligence/company" element={<ProtectedRoute><Layout><ServiceGuard serviceId="intelligence"><IntelligenceCompany key="sales-company" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/supplier/personas" element={<ProtectedRoute><Layout><ServiceGuard serviceId="supplier"><IntelligencePersonas key="supplier-personas" scope="supplier" titleKey="intel.personas_title_supplier" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/supplier/company" element={<ProtectedRoute><Layout><ServiceGuard serviceId="supplier"><IntelligenceCompany key="supplier-company" scope="supplier" titleKey="intel.company_title_supplier" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/network/tradeshow" element={<ProtectedRoute><Layout><ServiceGuard serviceId="network"><Tradeshow /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/network/events" element={<ProtectedRoute><Layout><ServiceGuard serviceId="network"><NetworkEvents /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/warroom" element={<ProtectedRoute><Layout><ServiceGuard serviceId="warroom"><WarRoom /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/war-room" element={<Navigate to="/warroom" replace />} />
      <Route path="/tech/patents" element={<ProtectedRoute><Layout><ServiceGuard serviceId="tech"><PatentsPage /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/tech/technologies" element={<ProtectedRoute><Layout><ServiceGuard serviceId="tech"><TechnologiesPage /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/tech/monitor" element={<ProtectedRoute><Layout><ServiceGuard serviceId="tech"><RDTechnologiesPage /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/customer-support" element={<ProtectedRoute><Layout><CustomerSupportPage /></Layout></ProtectedRoute>} />
      <Route path="/contract-twin" element={<ProtectedRoute><Layout><ContractTwin /></Layout></ProtectedRoute>} />
      <Route path="/due-diligence" element={<ProtectedRoute><Layout><DueDiligence /></Layout></ProtectedRoute>} />
      <Route path="/entity-loss-simulation" element={<ProtectedRoute><Layout><EntityLossSimulation /></Layout></ProtectedRoute>} />
      <Route path="/cyber-exposure" element={<ProtectedRoute><Layout><CyberExposure /></Layout></ProtectedRoute>} />
      <Route path="/live-world" element={<ProtectedRoute><Layout><LiveWorld /></Layout></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><Layout><ServiceGuard serviceId="normativa"><CompliancePage /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/normativa/dogane" element={<ProtectedRoute><Layout><ServiceGuard serviceId="normativa"><NormativaPage type="dogane" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/normativa/finanziaria" element={<ProtectedRoute><Layout><ServiceGuard serviceId="normativa"><NormativaPage type="finanziaria" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/normativa/commerciale" element={<ProtectedRoute><Layout><ServiceGuard serviceId="normativa"><NormativaPage type="commerciale" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/normativa/gare" element={<ProtectedRoute><Layout><ServiceGuard serviceId="normativa"><NormativaPage type="gare" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/academy/training" element={<ProtectedRoute><Layout><ServiceGuard serviceId="academy"><TrainingCourses /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/academy/documents" element={<ProtectedRoute><Layout><ServiceGuard serviceId="academy"><DocumentListPage
        titleKey="academy.docs.title" subtitleKey="academy.docs.subtitle" accentColor="#A78BFA" Icon={BookOpen}
        endpoint="academy/course-documents" collection="course_documents" facetField="category" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/library/knowledge" element={<ProtectedRoute><Layout><ServiceGuard serviceId="library"><DocumentListPage
        titleKey="lib.knowledge.title" subtitleKey="lib.knowledge.subtitle" accentColor="#00E5FF" Icon={Library}
        endpoint="library/knowledge" collection="knowledge_center" facetField="area" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/library/reports" element={<ProtectedRoute><Layout><ServiceGuard serviceId="library"><DocumentListPage
        titleKey="lib.reports.title" subtitleKey="lib.reports.subtitle" accentColor="#FF9F0A" Icon={FileText}
        endpoint="library/reports" collection="reports_docs" facetField="category" /></ServiceGuard></Layout></ProtectedRoute>} />
      <Route path="/library/repository" element={<ProtectedRoute><Layout><ServiceGuard serviceId="library"><DocumentListPage
        titleKey="lib.repository.title" subtitleKey="lib.repository.subtitle" accentColor="#00FF66" Icon={Archive}
        endpoint="library/repository" collection="repository" facetField="category" /></ServiceGuard></Layout></ProtectedRoute>} />

      {/* Admin (role === "admin" only). Non-admins get redirected to /dashboard. */}
      <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
        <Route index element={<Navigate to="contents" replace />} />
        <Route path="contents" element={<ContentsIndex />} />
        <Route path="contents/:name" element={<CollectionEditor />} />
        <Route path="limits" element={<LimitsSettings />} />
        <Route path="services" element={<ServicesSettings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AuthProvider>
              <ServicesProvider>
                <AppRoutes />
                <Toaster richColors position="top-right" />
              </ServicesProvider>
            </AuthProvider>
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
